import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getRestaurantCuisines } from "../../../../util/getRestaurantCuisines";
import { uploadDishImage, uploadHeroImage } from "../../../../util/imageApi";
import CuisinePicker from "../../../common/CuisinePicker";

// Styled components for modern UI
const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #212121;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #4CAF50;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #424242;
  margin-bottom: 8px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  font-size: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
  }

  &:read-only {
    background-color: #f5f5f5;
    color: #757575;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
  }
`;

const MenuItemCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #4CAF50;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const DragHandle = styled.div`
  color: #bdbdbd;
  cursor: grab;
  padding: 4px 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: center;

  &:hover {
    color: #757575;
  }

  &.grabbing {
    cursor: grabbing;
  }
`;

const MenuItemHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 500px) {
    flex-wrap: wrap;
  }
`;

const MenuItemInputGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 500px) {
    flex-basis: 100%;
  }
`;

const MenuItemNameInput = styled.input`
  font-size: 16px;
  font-weight: 600;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1);
  }
`;

const MenuItemDescInput = styled.textarea`
  font-size: 14px;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  min-height: 60px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1);
  }
`;

const ImageUploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const ImagePreview = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 4px;
  object-fit: cover;
  border: 1px solid #e0e0e0;
`;

const ImageButton = styled.button`
  font-size: 12px;
  padding: 4px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: white;
  color: #616161;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    border-color: #4CAF50;
    color: #4CAF50;
  }
`;

const ImageUrlInput = styled.input`
  font-size: 12px;
  padding: 4px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  flex: 1;
  min-width: 0;
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const ImageRemoveButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 14px;
  padding: 2px 6px;
  &:hover {
    color: #d32f2f;
  }
`;

const SectionDropdownContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const SectionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 13px;
  color: #616161;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: #eeeeee;
    border-color: #bdbdbd;
  }

  &:focus {
    outline: 2px solid #4CAF50;
    outline-offset: 2px;
  }

  i {
    flex-shrink: 0;
    font-size: 12px;
  }
`;

const SectionDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 200px;
  max-height: 300px;
  overflow-y: auto;
`;

const SectionSearchInput = styled.input`
  width: 100%;
  padding: 12px;
  border: none;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #9e9e9e;
  }
`;

const SectionOption = styled.div`
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #f5f5f5;
  }

  ${(props) =>
    props.isSelected &&
    `
    background-color: rgba(76, 175, 80, 0.1);
    font-weight: 500;
  `}

  ${(props) =>
    props.isCreate &&
    `
    color: #4CAF50;
    font-weight: 500;
    border-top: 1px solid #e0e0e0;
  `}

  i {
    color: #4CAF50;
  }
`;

const NoSectionOption = styled(SectionOption)`
  color: #9e9e9e;
  font-style: italic;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #bdbdbd;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #d32f2f;
    background: rgba(211, 47, 47, 0.1);
  }

  &:focus {
    outline: 2px solid #d32f2f;
    outline-offset: 2px;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #4CAF50;
  background: white;
  border: 2px dashed #4CAF50;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;

  &:hover {
    background: rgba(76, 175, 80, 0.05);
  }

  &:focus {
    outline: 2px solid #4CAF50;
    outline-offset: 2px;
  }

  i {
    font-size: 16px;
  }
`;

const SubmitButton = styled.input`
  display: block;
  width: 100%;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 32px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #e0e0e0;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const MenuSectionHeader = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #424242;
  margin-top: 20px;
  margin-bottom: 12px;
  padding: 10px 14px;
  background-color: #f5f5f5;
  border-radius: 6px;
  border-left: 3px solid #9E9E9E;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SectionHeaderText = styled.span`
  flex: 1;
`;

const SectionHeaderInput = styled.input`
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  color: #424242;
  padding: 4px 8px;
  border: 1px solid #4CAF50;
  border-radius: 4px;
  outline: none;
  background: white;

  &:focus {
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }
`;

const SectionEditButton = styled.button`
  background: none;
  border: none;
  color: #9E9E9E;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #4CAF50;
    background: rgba(76, 175, 80, 0.1);
  }

  &:focus {
    outline: 2px solid #4CAF50;
    outline-offset: 2px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #9e9e9e;

  i {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
`;

// Helper to extract section name
const getSectionName = (section) => {
  if (!section) return null;
  if (typeof section === "string") return section;
  return section.name || null;
};

// Group items by section, tracking global indices and stable drag IDs
const groupItemsBySection = (items) => {
  const grouped = {};
  items.forEach((item, index) => {
    const sectionName = getSectionName(item.section) || "__no_section__";
    if (!grouped[sectionName]) {
      grouped[sectionName] = [];
    }
    grouped[sectionName].push({ item, globalIndex: index, dragId: item._dragId });
  });
  return grouped;
};

// Sort section keys (no section last)
const sortSectionKeys = (keys) => {
  return keys.sort((a, b) => {
    if (a === "__no_section__") return 1;
    if (b === "__no_section__") return -1;
    return a.localeCompare(b);
  });
};

const HeroImageSection = ({ heroImage, restaurantId, onUploaded, onRemove, setAlert }) => {
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadHeroImage(file, null, restaurantId);
      onUploaded(result.displayUrl);
    } catch (err) {
      const msg = err.response?.data?.error || "Hero image upload failed";
      console.error("Hero image upload failed:", msg);
      setAlert && setAlert(msg, "failure");
    } finally {
      setUploading(false);
    }
  };

  const handleUrl = async () => {
    if (!urlInput.trim()) return;
    setUploading(true);
    try {
      const result = await uploadHeroImage(null, urlInput.trim(), restaurantId);
      onUploaded(result.displayUrl);
      setUrlInput("");
    } catch (err) {
      const msg = err.response?.data?.error || "Hero image upload failed";
      console.error("Hero image URL upload failed:", msg);
      setAlert && setAlert(msg, "failure");
    } finally {
      setUploading(false);
    }
  };

  if (heroImage) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <img
          src={heroImage}
          alt="Hero"
          style={{ width: 200, height: 56, objectFit: "cover", borderRadius: 6, border: "1px solid #e0e0e0" }}
        />
        <ImageRemoveButton type="button" onClick={onRemove} title="Remove hero image">✕</ImageRemoveButton>
      </div>
    );
  }

  if (uploading) {
    return <span style={{ fontSize: 13, color: "#999" }}>Uploading...</span>;
  }

  return (
    <ImageUploadRow>
      <input
        type="file"
        accept="image/*"
        ref={fileRef}
        style={{ display: "none" }}
        onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
      />
      <ImageButton type="button" onClick={() => fileRef.current?.click()}>
        📷 Upload Hero Image
      </ImageButton>
      <ImageUrlInput
        placeholder="or paste image URL"
        value={urlInput}
        onChange={(e) => setUrlInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleUrl(); } }}
      />
    </ImageUploadRow>
  );
};

const SortableSection = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners }, isDragging })}
    </div>
  );
};

const SortableMenuItemCard = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <MenuItemCard ref={setNodeRef} style={style}>
      <MenuItemHeader>
        <DragHandle
          {...attributes}
          {...listeners}
          className={isDragging ? "grabbing" : ""}
        >
          <i className="fas fa-grip-vertical" />
        </DragHandle>
        {children}
      </MenuItemHeader>
    </MenuItemCard>
  );
};

const RestaurantEditForm = ({
  currRestaurant,
  submitBackendCall,
  displayText,
  setAlert,
}) => {
  const dragIdCounter = useRef(0);
  const assignDragId = (item) => ({ ...item, _dragId: `drag-${dragIdCounter.current++}` });

  const [formData, setFormData] = useState(() => {
    const initialOptions = (currRestaurant?.veganOptions || [{}]).map(assignDragId);
    return {
      cuisines: [],
      restaurantName: currRestaurant?.name || "",
      website: currRestaurant?.website || "",
      address: typeof currRestaurant?.address === "string"
        ? currRestaurant.address
        : currRestaurant?.address?.formatted_address || "",
      veganOptions: initialOptions,
      heroImage: currRestaurant?.heroImage || "",
    };
  });
  const [otherCuisineText, setOtherCuisineText] = useState("");
  const [selectedCuisines, updateSelectedCuisines] = useState(
    currRestaurant?.cuisine
      ? currRestaurant.cuisine.reduce((a, v) => ({ ...a, [v]: v }), {})
      : {}
  );
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [sectionSearchText, setSectionSearchText] = useState("");
  const [editingSection, setEditingSection] = useState(null);
  const [editingSectionValue, setEditingSectionValue] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});

  const [sectionOrder, setSectionOrder] = useState(null);
  const dropdownRefs = useRef({});

  useEffect(function fetchCuisinesOnMount() {
    async function fetchCuisines() {
      const response = await getRestaurantCuisines();
      setFormData((prev) => ({ ...prev, cuisines: response }));
    }
    fetchCuisines();
  }, []);

  // Close dropdown when clicking outside
  useEffect(function closeDropdownOnClickOutside() {
    const handleClickOutside = (event) => {
      if (openDropdownIndex !== null) {
        const ref = dropdownRefs.current[openDropdownIndex];
        if (ref && !ref.contains(event.target)) {
          setOpenDropdownIndex(null);
          setSectionSearchText("");
          setDropdownStyle({});
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownIndex]);

  const [imageUploading, setImageUploading] = useState(null); // index of item currently uploading
  const [imageUrlInputs, setImageUrlInputs] = useState({}); // {index: "url text"} for paste-URL mode
  const imageFileInputRefs = useRef({});

  const handleImageUpload = async (index, file) => {
    if (!file || !currRestaurant?._id) return;
    setImageUploading(index);
    try {
      const result = await uploadDishImage(file, null, currRestaurant._id, index);
      updateVeganOption(index, "imageUrl", result.displayUrl);
    } catch (err) {
      const msg = err.response?.data?.error || "Image upload failed";
      console.error("Image upload failed:", msg);
      setAlert && setAlert(msg, "failure");
    } finally {
      setImageUploading(null);
    }
  };

  const handleImageUrlSubmit = async (index) => {
    const url = imageUrlInputs[index]?.trim();
    if (!url || !currRestaurant?._id) return;
    setImageUploading(index);
    try {
      const result = await uploadDishImage(null, url, currRestaurant._id, index);
      updateVeganOption(index, "imageUrl", result.displayUrl);
      setImageUrlInputs((prev) => ({ ...prev, [index]: "" }));
    } catch (err) {
      const msg = err.response?.data?.error || "Image upload failed";
      console.error("Image URL upload failed:", msg);
      setAlert && setAlert(msg, "failure");
    } finally {
      setImageUploading(null);
    }
  };

  const { cuisines, veganOptions, restaurantName, website, address } = formData;

  // Get all unique section names
  const getAvailableSections = () => {
    const sections = new Set();
    veganOptions.forEach((option) => {
      const sectionName = getSectionName(option.section);
      if (sectionName) {
        sections.add(sectionName);
      }
    });
    return Array.from(sections).sort();
  };

  const updateVeganOption = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      veganOptions: prev.veganOptions.map((option, i) =>
        i === index ? { ...option, [field]: value } : option
      ),
    }));
  };

  const updateOptionSection = (index, newSectionName) => {
    setFormData((prev) => ({
      ...prev,
      veganOptions: prev.veganOptions.map((option, i) =>
        i === index
          ? {
              ...option,
              section: newSectionName || null,
            }
          : option
      ),
    }));
    setOpenDropdownIndex(null);
    setSectionSearchText("");
    setDropdownStyle({});
  };

  // Calculate dropdown position for mobile to prevent off-screen overflow
  const calculateDropdownPosition = (index) => {
    const isMobile = window.innerWidth <= 500;
    if (!isMobile) {
      setDropdownStyle({});
      return;
    }

    const ref = dropdownRefs.current[index];
    if (ref) {
      const rect = ref.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: 16,
        right: 16,
      });
    }
  };

  const handleItemDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setFormData((prev) => {
      const oldIndex = prev.veganOptions.findIndex((o) => o._dragId === active.id);
      const newIndex = prev.veganOptions.findIndex((o) => o._dragId === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return {
        ...prev,
        veganOptions: arrayMove(prev.veganOptions, oldIndex, newIndex),
      };
    });
  };

  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setSectionOrder((prev) => {
      const keys = prev || sortSectionKeys(Object.keys(groupItemsBySection(veganOptions)));
      const oldIndex = keys.indexOf(active.id);
      const newIndex = keys.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return keys;
      return arrayMove(keys, oldIndex, newIndex);
    });
  };

  const deleteVeganOption = (index) => {
    setFormData((prev) => ({
      ...prev,
      veganOptions: prev.veganOptions.filter((_, i) => i !== index),
    }));
  };

  const addVeganOption = () => {
    setFormData((prev) => ({
      ...prev,
      veganOptions: [...prev.veganOptions, assignDragId({ option: "", description: "" })],
    }));
  };

  const handleSectionRename = (oldName, newName) => {
    if (!newName || newName.trim() === "" || oldName === newName) {
      setEditingSection(null);
      setEditingSectionValue("");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      veganOptions: prev.veganOptions.map((option) => {
        const currentSection = getSectionName(option.section);
        if (currentSection === oldName) {
          return { ...option, section: newName.trim() };
        }
        return option;
      }),
    }));

    setEditingSection(null);
    setEditingSectionValue("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const fixedCuisines = selectedCuisines.other
      ? { ...selectedCuisines, other: otherCuisineText.toLowerCase().trim() }
      : selectedCuisines;

    // Filter out empty options and normalize sections to {name, order} objects
    // Use the user's custom section ordering if they've reordered
    const sectionOrderMap = {};
    sectionKeys.forEach((key, i) => {
      if (key !== "__no_section__") sectionOrderMap[key] = i;
    });
    const validVeganOptions = veganOptions
      .filter((option) => option.option && option.option.trim() !== "")
      .map((option) => {
        const rawSection = option.section;
        const sectionName = getSectionName(rawSection);
        let section = null;
        if (sectionName) {
          section = { name: sectionName, order: sectionOrderMap[sectionName] ?? 0 };
        }
        return {
          option: option.option.trim(),
          description: option.description || "",
          section,
          ...(option.imageUrl ? { imageUrl: option.imageUrl } : {}),
        };
      });

    if (validVeganOptions.length === 0) {
      alert("Please add at least one vegan option before updating the restaurant");
      return;
    }

    const restaurant = {
      name: restaurantName,
      location: currRestaurant.coordinates,
      googleId: currRestaurant.googleId,
      veganOptions: validVeganOptions,
      cuisine: Object.values(fixedCuisines),
      metadata: currRestaurant.metadata || { isGoogle: true },
      status: currRestaurant.status || "RESTAURANT",
      website: website.trim() || undefined,
      address: address.trim() || undefined,
      ...(formData.heroImage ? { heroImage: formData.heroImage } : {}),
    };

    submitBackendCall(restaurant);
  };

  // Group items by section for display
  const groupedOptions = groupItemsBySection(veganOptions);
  const defaultSectionKeys = sortSectionKeys(Object.keys(groupedOptions));

  // Initialize or sync sectionOrder when sections change
  const sectionKeys = (() => {
    if (!sectionOrder) return defaultSectionKeys;
    // Keep custom order but add any new sections and remove deleted ones
    const currentSet = new Set(defaultSectionKeys);
    const filtered = sectionOrder.filter((k) => currentSet.has(k));
    const newSections = defaultSectionKeys.filter((k) => !sectionOrder.includes(k));
    return [...filtered, ...newSections];
  })();
  const availableSections = getAvailableSections();

  // Filter sections based on search text
  const filteredSections = availableSections.filter(
    (s) => s && s.toLowerCase().includes(sectionSearchText.toLowerCase())
  );

  // Check if search text is a new section name
  const isNewSection =
    sectionSearchText.trim() &&
    !availableSections.some(
      (s) => s && s.toLowerCase() === sectionSearchText.toLowerCase()
    );

  return (
    <form onSubmit={onSubmit}>
      <FormContainer>
        <FormSection>
          <SectionTitle>Restaurant Details</SectionTitle>
          <div style={{ marginBottom: "16px" }}>
            <FormLabel>Restaurant Name*</FormLabel>
            <TextInput
              type="text"
              value={restaurantName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, restaurantName: e.target.value }))
              }
              required
            />
          </div>
          <div style={{ marginBottom: "16px" }}>
            <FormLabel>Address</FormLabel>
            <TextInput
              type="text"
              value={address}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, address: e.target.value }))
              }
              placeholder="Restaurant address"
            />
          </div>
          <div>
            <FormLabel>Website</FormLabel>
            <TextInput
              type="text"
              placeholder="example.com"
              value={website}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, website: e.target.value }))
              }
            />
          </div>
        </FormSection>

        {currRestaurant?._id && (
          <FormSection>
            <SectionTitle>Hero Image</SectionTitle>
            <HeroImageSection
              heroImage={formData.heroImage}
              restaurantId={currRestaurant._id}
              onUploaded={(url) => setFormData((prev) => ({ ...prev, heroImage: url }))}
              onRemove={() => setFormData((prev) => ({ ...prev, heroImage: "" }))}
              setAlert={setAlert}
            />
          </FormSection>
        )}

        <FormSection>
          <SectionTitle>Vegan Options</SectionTitle>
          {veganOptions.length === 0 ? (
            <EmptyState>
              <i className="fas fa-utensils" />
              <p>No vegan options yet. Add your first one!</p>
            </EmptyState>
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
              <SortableContext items={sectionKeys} strategy={verticalListSortingStrategy}>
              {sectionKeys.map((sectionKey) => {
                const sectionItems = groupedOptions[sectionKey];
                const displaySectionName = sectionKey === "__no_section__" ? null : sectionKey;
                const isEditingSectionHeader = editingSection === sectionKey;
                const sortableIds = sectionItems.map(({ dragId }) => dragId);

                return (
                  <SortableSection key={sectionKey} id={sectionKey}>
                    {({ dragHandleProps, isDragging: isSectionDragging }) => (
                    <>
                    {displaySectionName && (
                      <MenuSectionHeader>
                        <DragHandle
                          {...dragHandleProps}
                          className={isSectionDragging ? "grabbing" : ""}
                        >
                          <i className="fas fa-grip-vertical" />
                        </DragHandle>
                        {isEditingSectionHeader ? (
                          <SectionHeaderInput
                            value={editingSectionValue}
                            onChange={(e) => setEditingSectionValue(e.target.value)}
                            onBlur={() => handleSectionRename(displaySectionName, editingSectionValue)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSectionRename(displaySectionName, editingSectionValue);
                              if (e.key === "Escape") {
                                setEditingSection(null);
                                setEditingSectionValue("");
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          <>
                            <SectionHeaderText>{displaySectionName}</SectionHeaderText>
                            <SectionEditButton
                              type="button"
                              onClick={() => {
                                setEditingSection(sectionKey);
                                setEditingSectionValue(displaySectionName);
                              }}
                              title="Rename section"
                            >
                              <i className="fas fa-pencil-alt" />
                            </SectionEditButton>
                          </>
                        )}
                      </MenuSectionHeader>
                    )}

                    <DndContext collisionDetection={closestCenter} onDragEnd={handleItemDragEnd}>
                    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                      {sectionItems.map(({ item: option, globalIndex: originalIndex, dragId }) => {
                        const currentSectionName = getSectionName(option.section);
                        const isDropdownOpen = openDropdownIndex === originalIndex;

                        return (
                          <SortableMenuItemCard key={dragId} id={dragId}>
                            <MenuItemInputGroup>
                              <MenuItemNameInput
                                type="text"
                                placeholder="Item name"
                                value={option.option || ""}
                                onChange={(e) =>
                                  updateVeganOption(originalIndex, "option", e.target.value)
                                }
                                required
                              />
                              <MenuItemDescInput
                                placeholder="Description (optional)"
                                value={option.description || ""}
                                onChange={(e) =>
                                  updateVeganOption(originalIndex, "description", e.target.value)
                                }
                              />
                              {currRestaurant?._id && (
                                <ImageUploadRow>
                                  {option.imageUrl ? (
                                    <>
                                      <ImagePreview src={option.imageUrl} alt="" />
                                      <ImageRemoveButton
                                        type="button"
                                        onClick={() => updateVeganOption(originalIndex, "imageUrl", "")}
                                        title="Remove image"
                                      >
                                        ✕
                                      </ImageRemoveButton>
                                    </>
                                  ) : imageUploading === originalIndex ? (
                                    <span style={{ fontSize: 12, color: "#999" }}>Uploading...</span>
                                  ) : (
                                    <>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        ref={(el) => (imageFileInputRefs.current[originalIndex] = el)}
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                          if (e.target.files[0]) handleImageUpload(originalIndex, e.target.files[0]);
                                        }}
                                      />
                                      <ImageButton
                                        type="button"
                                        onClick={() => imageFileInputRefs.current[originalIndex]?.click()}
                                      >
                                        📷 Upload
                                      </ImageButton>
                                      <ImageUrlInput
                                        placeholder="or paste image URL"
                                        value={imageUrlInputs[originalIndex] || ""}
                                        onChange={(e) =>
                                          setImageUrlInputs((prev) => ({ ...prev, [originalIndex]: e.target.value }))
                                        }
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleImageUrlSubmit(originalIndex);
                                          }
                                        }}
                                      />
                                    </>
                                  )}
                                </ImageUploadRow>
                              )}
                            </MenuItemInputGroup>

                            <SectionDropdownContainer
                              ref={(el) => (dropdownRefs.current[originalIndex] = el)}
                            >
                              <SectionButton
                                type="button"
                                onClick={() => {
                                  if (isDropdownOpen) {
                                    setOpenDropdownIndex(null);
                                    setDropdownStyle({});
                                  } else {
                                    setOpenDropdownIndex(originalIndex);
                                    calculateDropdownPosition(originalIndex);
                                  }
                                  setSectionSearchText("");
                                }}
                                title={currentSectionName || "No section - click to assign"}
                              >
                                <i className="fas fa-folder" />
                                <span>{currentSectionName || "No section"}</span>
                                <i className={`fas fa-chevron-${isDropdownOpen ? "up" : "down"}`} />
                              </SectionButton>

                              {isDropdownOpen && (
                                <SectionDropdown style={dropdownStyle}>
                                  <SectionSearchInput
                                    type="text"
                                    placeholder="Search or create section..."
                                    value={sectionSearchText}
                                    onChange={(e) => setSectionSearchText(e.target.value)}
                                    autoFocus
                                  />

                                  <NoSectionOption
                                    isSelected={!currentSectionName}
                                    onClick={() => updateOptionSection(originalIndex, null)}
                                  >
                                    No section
                                  </NoSectionOption>

                                  {filteredSections.map((secName) => (
                                    <SectionOption
                                      key={secName}
                                      isSelected={secName === currentSectionName}
                                      onClick={() => updateOptionSection(originalIndex, secName)}
                                    >
                                      {secName}
                                    </SectionOption>
                                  ))}

                                  {isNewSection && (
                                    <SectionOption
                                      isCreate
                                      onClick={() =>
                                        updateOptionSection(originalIndex, sectionSearchText.trim())
                                      }
                                    >
                                      <i className="fas fa-plus" />
                                      Create "{sectionSearchText.trim()}"
                                    </SectionOption>
                                  )}
                                </SectionDropdown>
                              )}
                            </SectionDropdownContainer>

                            <DeleteButton
                              type="button"
                              onClick={() => deleteVeganOption(originalIndex)}
                              title="Remove item"
                            >
                              <i className="fas fa-trash" />
                            </DeleteButton>
                          </SortableMenuItemCard>
                        );
                      })}
                    </SortableContext>
                    </DndContext>
                    </>
                    )}
                  </SortableSection>
                );
              })}
              </SortableContext>
            </DndContext>
          )}

          <AddButton type="button" onClick={addVeganOption}>
            <i className="fas fa-plus" />
            Add Vegan Option
          </AddButton>
        </FormSection>

        <FormSection>
          <SectionTitle>Cuisine Type</SectionTitle>
          <CuisinePicker
            className="form-control noFlex"
            passedCuisines={cuisines}
            selectedCuisines={selectedCuisines}
            otherCuisineText={otherCuisineText}
            updateSelectedCuisines={updateSelectedCuisines}
            setOtherCuisineText={setOtherCuisineText}
          />
        </FormSection>

        <SubmitButton type="submit" value={displayText || "Update Restaurant"} />
      </FormContainer>
    </form>
  );
};

const mapStateToProps = () => ({});
export default connect(mapStateToProps, {})(RestaurantEditForm);
